<template>
  <div class="card-modern">

    <!-- HEADER -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h3 class="card-title-modern">Report Overdue Perjanjian Kerja Sama</h3>
        <!-- <small class="text-muted">Daftar dokumen perjanjian kerja sama yang sudah melewati tanggal selesai kerjasama.</small> -->
      </div>
    </div>

    <!-- FILTER + EXPORT -->
    <div class="d-flex align-items-end justify-content-end gap-2 mb-3 flex-wrap">
      <button class="btn btn-sm bg-success-subtle border border-success-subtle text-success-emphasis"
        style="border-radius: 8px;" :disabled="exporting" @click="exportFile('excel')">
        <span v-if="exporting === 'excel'">
          <span class="spinner-border spinner-border-sm me-1"></span>Mengunduh...
        </span>
        <span v-else><i class="bi bi-file-earmark-excel me-1"></i>Export Excel</span>
      </button>

      <button class="btn btn-sm bg-danger-subtle border border-danger-subtle text-danger-emphasis"
        style="border-radius: 8px;" :disabled="exporting" @click="exportFile('pdf')">
        <span v-if="exporting === 'pdf'">
          <span class="spinner-border spinner-border-sm me-1"></span>Mengunduh...
        </span>
        <span v-else><i class="bi bi-file-earmark-pdf me-1"></i>Export PDF</span>
      </button>

      <div style="width: 170px;">
        <label class="form-label small text-muted mb-1">Tanggal Mulai</label>
        <input type="date" v-model="filters.startDate" class="form-control" style="border-radius: 8px;" />
      </div>
      <div style="width: 170px;">
        <label class="form-label small text-muted mb-1">Tanggal Akhir</label>
        <input type="date" v-model="filters.endDate" class="form-control" style="border-radius: 8px;" />
      </div>
      <button class="button-modern" @click="fetchData(1)">
        <i class="bi bi-search me-1"></i>Cari
      </button>
      <button class="btn btn-sm bg-light border text-dark" style="border-radius: 8px; height: 38px;" @click="resetFilter">
        Reset
      </button>
    </div>

    <!-- TABLE -->
    <div class="modern-table-wrapper">
      <table class="table-modern">
        <thead>
          <tr>
            <th>No</th>
            <th>Nama Perusahaan</th>
            <th>Partner Type</th>
            <th>Tanggal Mulai</th>
            <th>Tanggal Selesai</th>
            <th class="text-center">Overdue (Hari)</th>
          </tr>
        </thead>
        <tbody>

          <!-- LOADING -->
          <tr v-if="loading">
            <td colspan="6" class="text-center py-4">
              <div class="spinner-border spinner-border-sm me-2"></div>
              Memuat data...
            </td>
          </tr>

          <!-- EMPTY -->
          <tr v-else-if="data.length === 0">
            <td colspan="6" class="text-center py-4 text-muted">
              Tidak ada dokumen overdue
            </td>
          </tr>

          <!-- DATA -->
          <tr v-else v-for="(item, index) in data" :key="item.document_id">
            <td>{{ (pagination.page - 1) * pagination.limit + index + 1 }}</td>
            <td><span class="fw">{{ item.nama_perusahaan }}</span></td>
            <td>{{ item.partner_type }}</td>
            <td>{{ item.tanggal_mulai_kerjasama }}</td>
            <td>{{ item.tanggal_selesai_kerjasama }}</td>
            <td class="text-center">
              <span class="badge-modern badge-danger">{{ item.overdue_days }} Hari</span>
            </td>
          </tr>

        </tbody>
      </table>
    </div>

    <!-- PAGINATION -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <small class="text-muted">
        Menampilkan {{ data.length === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1 }}
        - {{ Math.min(pagination.page * pagination.limit, pagination.total) }} dari {{ pagination.total }} data
      </small>
      <div class="d-flex align-items-center gap-2">
        <small class="text-muted">Rows per page</small>
        <select v-model.number="pagination.limit" class="form-select form-select-sm" style="width: 70px;" @change="changePerPage">
          <option v-for="opt in perPageOptions" :key="opt" :value="opt">{{ opt }}</option>
        </select>
        <nav>
          <ul class="pagination pagination-sm mb-0">
            <li class="page-item" :class="{ disabled: pagination.page === 1 }">
              <button class="page-link" @click="fetchData(pagination.page - 1)">&#8249;</button>
            </li>
            <li v-for="(p, idx) in visiblePages" :key="idx" class="page-item"
              :class="{ active: p === pagination.page, disabled: p === '...' }">
              <button class="page-link" @click="p !== '...' && fetchData(p)">{{ p }}</button>
            </li>
            <li class="page-item" :class="{ disabled: pagination.page === totalPages }">
              <button class="page-link" @click="fetchData(pagination.page + 1)">&#8250;</button>
            </li>
          </ul>
        </nav>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import reportOverdueService from '@/services/reportOverdueService';

const data = ref([]);
const loading = ref(false);
const exporting = ref(null);

const filters = reactive({
  startDate: '',
  endDate: ''
});

const pagination = reactive({
  page: 1,
  limit: 10,
  total: 0
});

const perPageOptions = [10, 25, 50, 100];

const totalPages = computed(() =>
  Math.ceil(pagination.total / pagination.limit) || 1
);

// Sama seperti pola visiblePages di CoaView.vue — max 5 nomor halaman + ellipsis
const visiblePages = computed(() => {
  const pages = [];
  const total = totalPages.value;
  const current = pagination.page;
  const delta = 1;

  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
    return pages;
  }

  pages.push(1);
  if (current - delta > 2) pages.push('...');

  for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
    pages.push(i);
  }

  if (current + delta < total - 1) pages.push('...');
  pages.push(total);

  return pages;
});

const buildParams = (extra = {}) => {
  const params = { ...extra };
  if (filters.startDate) params.startDate = filters.startDate;
  if (filters.endDate) params.endDate = filters.endDate;
  return params;
};

const fetchData = async (page = 1) => {
  loading.value = true;
  try {
    const res = await reportOverdueService.getOverdue(
      buildParams({ page, limit: pagination.limit })
    );
    data.value = res.data.data;
    pagination.page = res.data.pagination.page;
    pagination.total = res.data.pagination.total;
  } catch (err) {
    console.error('Gagal mengambil data report overdue', err);
    alert('Gagal mengambil data report overdue');
  } finally {
    loading.value = false;
  }
};

const changePerPage = () => {
  fetchData(1);
};

const resetFilter = () => {
  filters.startDate = '';
  filters.endDate = '';
  fetchData(1);
};

const exportFile = async (type) => {
  exporting.value = type;
  try {
    const res = type === 'excel'
      ? await reportOverdueService.exportExcel(buildParams())
      : await reportOverdueService.exportPDF(buildParams());

    const blob = new Blob([res.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Report_Overdue_PKS.${type === 'excel' ? 'xlsx' : 'pdf'}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error(`Gagal export ${type}`, err);
    alert(`Gagal export ${type}`);
  } finally {
    exporting.value = null;
  }
};

onMounted(() => {
  fetchData(1);
});
</script>